import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { SearchableMultiSelect } from "@/components/ui/searchable-multi-select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { PageHeader, DataTable, StatusBadge, EmptyState } from "@/components/ui-system";
import { toast } from "sonner";
import {
  AlertTriangle, RefreshCw, Search, Filter, Download, Loader2, Flag,
  Settings, Mail, Plus, Trash2, Clock, ExternalLink,
} from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";

const ITEMS_PER_PAGE = 20;

// Persist the page's view state (active tab + all filters + pagination) to localStorage so it
// survives navigating away / refresh. Bump the version suffix to invalidate old shapes.
const LS_KEY = "commercialRedFlags.state.v1";
const loadPersisted = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}") || {}; }
  catch { return {}; }
};

const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const orderUrl = (r) => `/admin/orders/${r.order_id || r.entity_id || ""}`;

// Human label for a flag type's auto-claim component config (undefined => on by default).
const claimBasisLabel = (t) => {
  const parts = [];
  if (t.claim_demurrage !== false) parts.push("Demurrage");
  if (t.claim_demurrage_v2 !== false) parts.push("Demurrage V2");
  if (t.claim_appointment !== false) parts.push("Appointment");
  return parts.length ? parts.join(" + ") : "None";
};

const FLAG_STATUS_COLORS = {
  active: "bg-amber-100 text-amber-700",
  resolved: "bg-green-100 text-green-700",
  cancelled: "bg-rose-100 text-rose-700",
  ignored: "bg-slate-100 text-slate-600",
};
const PRIORITY_COLORS = {
  critical: "bg-red-100 text-red-700",
  high: "bg-orange-100 text-orange-700",
  medium: "bg-yellow-100 text-yellow-700",
  low: "bg-slate-100 text-slate-600",
};

// A label with its timestamp stacked as date (row 1) + time (row 2).
const TS = ({ label, value }) => {
  const parts = value ? formatToIST(value).split(/,\s+/) : [];
  return (
    <div className="flex justify-between gap-3 leading-tight items-start">
      <span className="text-[11px] text-slate-400 whitespace-nowrap pt-0.5">{label}</span>
      {parts.length ? (
        <div className="text-right whitespace-nowrap">
          <div className="text-xs font-medium text-slate-700">{parts[0]}</div>
          {parts.length > 1 && <div className="text-[11px] text-slate-500">{parts.slice(1).join(", ")}</div>}
        </div>
      ) : <span className="text-xs text-slate-400">—</span>}
    </div>
  );
};

// A compact label:value row for the stacked (multi-row) table cells.
const KV = ({ label, value }) => (
  <div className="flex justify-between gap-3 leading-tight">
    <span className="text-[11px] text-slate-400 whitespace-nowrap">{label}</span>
    <span className="text-xs font-medium text-slate-700 text-right whitespace-nowrap">{value ?? "—"}</span>
  </div>
);

// Like KV but the value is an arbitrary node (used for the clickable LRN / Order links).
const IDField = ({ label, children }) => (
  <div className="flex justify-between gap-3 leading-tight">
    <span className="text-[11px] text-slate-400 whitespace-nowrap">{label}</span>
    <div className="text-xs font-medium text-slate-700 text-right whitespace-nowrap">{children}</div>
  </div>
);

// Commercial Red Flags — carrier claims on B2B freight (demurrage / appointment charges).
const CommercialRedFlags = ({ user, onLogout }) => {
  const isAdmin = user?.role === "admin";

  // Restore persisted view state once on mount.
  const [persisted] = useState(loadPersisted);
  const [activeTab, setActiveTab] = useState(() => {
    const t = persisted.activeTab;
    return (t === "config" || t === "recipients") && user?.role !== "admin" ? "flags" : (t || "flags");
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(persisted.page || 1);
  const [exporting, setExporting] = useState(false);

  const [searchQuery, setSearchQuery] = useState(persisted.searchQuery || "");
  const [debouncedSearch, setDebouncedSearch] = useState(persisted.searchQuery || "");

  const [sellerFilters, setSellerFilters] = useState(persisted.sellerFilters || []);
  const [partnerFilters, setPartnerFilters] = useState(persisted.partnerFilters || []);
  const [statusFilters, setStatusFilters] = useState(persisted.statusFilters || []);
  const [flagState, setFlagState] = useState(persisted.flagState || "all"); // all | flagged | unflagged
  const [flagSource, setFlagSource] = useState(persisted.flagSource || "all"); // all | auto | manual
  const [flagCategory, setFlagCategory] = useState(persisted.flagCategory || []);
  // Sub-classification of the ACTIVE flags. "all" keeps every active flag; the other
  // two split it by whether the claim has been filed with the carrier yet.
  const [claimProgress, setClaimProgress] = useState(persisted.claimProgress || "all");
  const [startDate, setStartDate] = useState(persisted.startDate || "");
  const [endDate, setEndDate] = useState(persisted.endDate || "");
  const [filterOptions, setFilterOptions] = useState({ sellers: [], shipping_partners: [], statuses: [], summary: {} });
  const [filtersExpanded, setFiltersExpanded] = useState(persisted.filtersExpanded || false);

  // Detail / resolution-thread modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailFlag, setDetailFlag] = useState(null);
  const [availableActions, setAvailableActions] = useState([]);
  const [actionType, setActionType] = useState("");
  const [actionNotes, setActionNotes] = useState("");
  const [submittingAction, setSubmittingAction] = useState(false);

  // Raise (manual) dialog
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [raiseRow, setRaiseRow] = useState(null);
  const [raiseType, setRaiseType] = useState("");
  const [raiseNotes, setRaiseNotes] = useState("");
  const [submittingRaise, setSubmittingRaise] = useState(false);

  // Config + recipients (admin)
  const [flagTypes, setFlagTypes] = useState([]);
  const [settings, setSettings] = useState(null);
  const [checkInterval, setCheckInterval] = useState(60);
  const [savingSettings, setSavingSettings] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [recipientOpen, setRecipientOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [savingRecipient, setSavingRecipient] = useState(false);

  const resetPage = () => setPage((p) => (p === 1 ? p : 1));

  const sellerKey = sellerFilters.join(",");
  const partnerKey = partnerFilters.join(",");
  const statusKey = statusFilters.join(",");
  const categoryKey = flagCategory.join(",");
  const activeFilterCount = sellerFilters.length + partnerFilters.length + statusFilters.length
    + flagCategory.length + (flagState !== "all" ? 1 : 0) + (flagSource !== "all" ? 1 : 0)
    + (claimProgress !== "all" ? 1 : 0)
    + (startDate ? 1 : 0) + (endDate ? 1 : 0);

  const buildParams = (includePaging) => {
    const p = {};
    if (debouncedSearch.trim()) p.search = debouncedSearch.trim();
    if (sellerFilters.length) p.seller_id = sellerFilters.join(",");
    if (partnerFilters.length) p.shipping_partner_id = partnerFilters.join(",");
    if (statusFilters.length) p.status = statusFilters.join(",");
    if (flagState !== "all") p.flag_state = flagState;
    if (flagSource !== "all") p.flag_source = flagSource;
    if (flagCategory.length) p.flag_category = flagCategory.join(",");
    if (claimProgress !== "all") p.claim_progress = claimProgress;
    if (startDate) p.start_date = startDate;
    if (endDate) p.end_date = endDate;
    if (includePaging) { p.page = page; p.limit = ITEMS_PER_PAGE; }
    return p;
  };

  const fetchFilterOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/commercial-flags/candidates/filter-options`);
      setFilterOptions(res.data || { sellers: [], shipping_partners: [], statuses: [], summary: {} });
    } catch { console.error("Failed to fetch filter options"); }
  }, []);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/commercial-flags/candidates`, { params: buildParams(true) });
      setRows(res.data.flags || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch { toast.error("Failed to fetch commercial flags"); }
    finally { setLoading(false); }
  // eslint-disable-next-line
  }, [debouncedSearch, sellerKey, partnerKey, statusKey, flagState, flagSource, categoryKey,
      claimProgress, startDate, endDate, page]);

  // Debounce free-text search. Skip the very first run (mount) so a restored search value
  // doesn't reset the restored page number.
  const firstSearchRun = useRef(true);
  useEffect(() => {
    if (firstSearchRun.current) { firstSearchRun.current = false; return; }
    const t = setTimeout(() => { setDebouncedSearch(searchQuery); resetPage(); }, 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Persist the view state whenever any tracked piece changes.
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({
        activeTab, searchQuery, sellerFilters, partnerFilters, statusFilters,
        flagState, flagSource, flagCategory, claimProgress, startDate, endDate,
        filtersExpanded, page,
      }));
    } catch { /* ignore quota / private-mode errors */ }
  }, [activeTab, searchQuery, sellerFilters, partnerFilters, statusFilters,
      flagState, flagSource, flagCategory, claimProgress, startDate, endDate,
      filtersExpanded, page]);

  useEffect(() => { fetchFilterOptions(); }, [fetchFilterOptions]);
  useEffect(() => { fetchCandidates(); }, [fetchCandidates]);

  useEffect(() => {
    axios.get(`${API}/commercial-flags/actions/available`).then((r) => setAvailableActions(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchFlagTypes(); }, []); // for the Flag Category filter (view-permitted)

  useEffect(() => {
    if (!isAdmin) return;
    fetchSettings();
    fetchRecipients();
  // eslint-disable-next-line
  }, [isAdmin]);

  const fetchFlagTypes = async () => {
    try { setFlagTypes((await axios.get(`${API}/commercial-flags/types/all`)).data || []); }
    catch { console.error("Failed to fetch flag types"); }
  };
  const fetchSettings = async () => {
    try {
      const r = await axios.get(`${API}/commercial-flags/settings`);
      setSettings(r.data);
      setCheckInterval(r.data?.check_interval_minutes || 60);
    } catch { console.error("Failed to fetch settings"); }
  };
  const fetchRecipients = async () => {
    try { setRecipients((await axios.get(`${API}/commercial-flags/recipients/all`)).data || []); }
    catch { console.error("Failed to fetch recipients"); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/commercial-flags/candidates/export.csv`, { params: buildParams(false), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `commercial_red_flags_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch { toast.error("Failed to export"); }
    finally { setExporting(false); }
  };

  const clearFilters = () => {
    setSellerFilters([]); setPartnerFilters([]); setStatusFilters([]);
    setFlagState("all"); setFlagSource("all"); setFlagCategory([]);
    setClaimProgress("all");
    setStartDate(""); setEndDate(""); resetPage();
  };

  // Clicking a summary count chip toggles the matching filter.
  const toggleSource = (s) => { setFlagSource((cur) => (cur === s ? "all" : s)); resetPage(); };

  // Action Required and Action Taken are the two halves of the ACTIVE flags, so either
  // one also puts Flag State on "flagged". Clicking the selected chip clears the split
  // and returns to ALL active flags - which is why flagged is set on both paths.
  const toggleProgress = (p) => {
    setClaimProgress((cur) => (cur === p ? "all" : p));
    setFlagState("flagged");
    resetPage();
  };
  const toggleCategory = (id) => {
    setFlagCategory((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
    resetPage();
  };

  const link = (value, r) =>
    value ? (
      <a href={orderUrl(r)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{value}</a>
    ) : "—";

  // ── Detail / resolution thread ────────────────────────────────────────────
  const openDetail = async (flagId) => {
    try {
      const r = await axios.get(`${API}/commercial-flags/flags/${flagId}`);
      setDetailFlag(r.data);
      setActionType(""); setActionNotes("");
      setDetailOpen(true);
    } catch { toast.error("Failed to load flag"); }
  };

  const handleTakeAction = async () => {
    if (!actionType) { toast.error("Select an action"); return; }
    setSubmittingAction(true);
    try {
      await axios.post(`${API}/commercial-flags/flags/${detailFlag.id}/action`, { action_type: actionType, notes: actionNotes });
      toast.success(`Action '${actionType}' recorded`);
      setActionType(""); setActionNotes("");
      await openDetail(detailFlag.id);
      fetchCandidates(); fetchFilterOptions();
    } catch { toast.error("Failed to record action"); }
    finally { setSubmittingAction(false); }
  };

  // ── Manual raise ──────────────────────────────────────────────────────────
  const raisableTypes = (row) => {
    const taken = new Set((row?.flags || []).map((f) => f.flag_type_id));
    return flagTypes.length
      ? flagTypes.filter((t) => !taken.has(t.id))
      // Fallback if the admin type list isn't loaded (non-admin can still raise via act perm).
      : [{ id: "reach_after_edd", name: "Reached Destination After EDD" }, { id: "missed_appointment", name: "Missed Appointment" }]
        .filter((t) => !taken.has(t.id));
  };

  const openRaise = (row) => {
    const options = raisableTypes(row);
    setRaiseRow(row);
    setRaiseType(options[0]?.id || "");
    setRaiseNotes("");
    setRaiseOpen(true);
  };

  const handleRaise = async () => {
    if (!raiseType) { toast.error("Select a category"); return; }
    setSubmittingRaise(true);
    try {
      const res = await axios.post(`${API}/commercial-flags/flags/raise`, {
        order_id: raiseRow.order_id, flag_type_id: raiseType, notes: raiseNotes,
      });
      if (res.data.success) toast.success("Commercial flag raised");
      else toast.message(res.data.message || "Already flagged");
      setRaiseOpen(false);
      fetchCandidates(); fetchFilterOptions();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to raise flag"); }
    finally { setSubmittingRaise(false); }
  };

  // ── Config / recipients ───────────────────────────────────────────────────
  const handleUpdateSettings = async () => {
    if (checkInterval < 1 || checkInterval > 1440) { toast.error("Interval must be 1–1440 minutes"); return; }
    setSavingSettings(true);
    try {
      await axios.put(`${API}/commercial-flags/settings`, { check_interval_minutes: checkInterval });
      toast.success("Settings updated"); fetchSettings();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update settings"); }
    finally { setSavingSettings(false); }
  };
  const handleTriggerCheck = async () => {
    setTriggering(true);
    try {
      const r = await axios.post(`${API}/commercial-flags/trigger-check`);
      toast.success(r.data.message || "Check complete");
      fetchCandidates(); fetchFilterOptions(); fetchSettings();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to run check"); }
    finally { setTriggering(false); }
  };
  const handleToggleType = async (type, enabled) => {
    try {
      await axios.put(`${API}/commercial-flags/types/${type.id}`, { enabled });
      fetchFlagTypes();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update flag type"); }
  };
  const handleUpdateClaimComponents = async (type, patch) => {
    try {
      await axios.put(`${API}/commercial-flags/types/${type.id}`, patch);
      fetchFlagTypes();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update claim config"); }
  };
  const adjustComponent = async (component, included) => {
    try {
      await axios.post(`${API}/commercial-flags/flags/${detailFlag.id}/components`, { component, included });
      toast.success(included ? "Added to claim" : "Removed from claim");
      await openDetail(detailFlag.id);
      fetchCandidates(); fetchFilterOptions();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update claim"); }
  };
  const handleAddRecipient = async () => {
    if (!newEmail) { toast.error("Enter an email"); return; }
    setSavingRecipient(true);
    try {
      await axios.post(`${API}/commercial-flags/recipients`, { email: newEmail, name: newName, flag_types: [] });
      toast.success("Recipient added"); setRecipientOpen(false); setNewEmail(""); setNewName(""); fetchRecipients();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add recipient"); }
    finally { setSavingRecipient(false); }
  };
  const handleToggleRecipient = async (id, enabled) => {
    try { await axios.put(`${API}/commercial-flags/recipients/${id}?enabled=${enabled}`); fetchRecipients(); }
    catch { toast.error("Failed to update recipient"); }
  };
  const handleDeleteRecipient = async (id) => {
    try { await axios.delete(`${API}/commercial-flags/recipients/${id}`); toast.success("Recipient deleted"); fetchRecipients(); }
    catch { toast.error("Failed to delete recipient"); }
  };

  // ── Claim / flag column ───────────────────────────────────────────────────
  const flagCell = (r) => {
    const flags = r.flags || [];
    const options = raisableTypes(r);
    return (
      <div className="space-y-1.5 min-w-[150px]">
        {flags.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => openDetail(f.id)}
            className="block w-full text-left rounded-md border border-slate-200 hover:border-slate-300 px-2 py-1 transition-colors"
            title="View claim / resolution thread"
          >
            <div className="flex items-center justify-between gap-2">
              <span className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${FLAG_STATUS_COLORS[f.status] || "bg-slate-100 text-slate-600"}`}>
                {(f.status || "").toUpperCase()}
              </span>
              <span className="text-xs font-semibold text-slate-700 whitespace-nowrap">{money(f.claim_amount)}</span>
            </div>
            <div className="text-[11px] text-slate-500 truncate mt-0.5">{f.flag_type_name}</div>
          </button>
        ))}
        {options.length > 0 && (
          <Button variant="outline" size="sm" className="h-7 w-full text-xs" onClick={() => openRaise(r)}>
            <Flag className="w-3 h-3 mr-1" /> Raise
          </Button>
        )}
      </div>
    );
  };

  const columns = [
    { key: "identity", header: "Partner / LRN / Order / Seller", cell: (r) => (
        <div className="space-y-1 min-w-[210px]">
          <IDField label="Partner">{r.shipping_partner_name || "—"}{r.actual_shipping_partner ? ` (${r.actual_shipping_partner})` : ""}</IDField>
          <IDField label="LRN">{link(r.lrn, r)}</IDField>
          <IDField label="Order">{link(r.order_number, r)}</IDField>
          <IDField label="Seller">{r.seller_name || "—"}</IDField>
        </div>
      ) },
    { key: "claim", header: "Claim / Flag", cell: flagCell },
    { key: "flag_reason", header: "Flag Reason", cell: (r) => {
        const flags = r.flags || [];
        if (!flags.length) return <span className="text-sm text-slate-400">—</span>;
        return (
          <div className="space-y-1.5 min-w-[240px] max-w-[340px]">
            {flags.map((f) => (
              <div key={f.id} className="text-xs text-slate-600 leading-snug whitespace-normal">{f.reason}</div>
            ))}
          </div>
        );
      } },
    { key: "demurrage", header: "Demurrage", align: "right", cell: (r) => <span className="text-sm font-semibold text-red-600 whitespace-nowrap">{money(r.demurrage_amount)}</span> },
    { key: "demurrage_v2", header: "Demurrage V2", align: "right", cell: (r) => <span className="text-sm font-semibold text-red-600 whitespace-nowrap">{money(r.demurrage_v2_amount)}</span> },
    { key: "appt_charge", header: "Appt. Charge", align: "right", cell: (r) => <span className="text-sm font-medium text-slate-700 whitespace-nowrap">{money(r.appointment_charge)}</span> },
    { key: "lifecycle", header: "Life Cycle", cell: (r) => (
        <div className="space-y-1.5 min-w-[190px]">
          <TS label="Manifest" value={r.manifested_at} />
          <TS label="Reached Dest." value={r.reach_destination_at} />
          <TS label="Delivered" value={r.delivered_at} />
        </div>
      ) },
    { key: "return", header: "Return", cell: (r) => (
        <div className="space-y-1.5 min-w-[190px]">
          <TS label="Returned" value={r.returned_at} />
          <TS label="Return Delivered" value={r.return_delivered_at} />
        </div>
      ) },
    { key: "edds", header: "All EDDs", cell: (r) => (
        (r.edds && r.edds.length) ? (
          <div className="space-y-0.5 min-w-[110px]">
            {r.edds.map((d, i) => <div key={i} className="text-xs text-slate-700 whitespace-nowrap">{d}</div>)}
          </div>
        ) : <span className="text-sm text-slate-400">—</span>
      ) },
    { key: "po_block", header: "PO / Commercial", cell: (r) => (
        <div className="space-y-0.5 min-w-[190px]">
          <KV label="PO / Ref" value={r.po_number || "—"} />
          <KV label="PO Date" value={r.po_date || "—"} />
          <KV label="PO Expiry" value={r.po_expiry_date || "—"} />
          <KV label="Invoice" value={money(r.invoice_amount)} />
        </div>
      ) },
    { key: "status", header: "Status", cell: (r) => <StatusBadge status={r.status} kind="order" /> },
    { key: "appointment", header: "Appointment", cell: (r) => (
        r.appointment_date ? (
          <div className="leading-tight whitespace-nowrap">
            <div className="text-sm text-slate-700">{r.appointment_date}</div>
            {r.appointment_time_range && <div className="text-xs text-slate-500">{r.appointment_time_range}</div>}
          </div>
        ) : <span className="text-sm text-slate-400">—</span>
      ) },
  ];

  const summary = filterOptions.summary || {};

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full">
        <PageHeader
          title="Commercial Red Flags"
          subtitle="Carrier claims on B2B freight — demurrage & appointment charges"
          actions={
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || total === 0}>
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Export
              </Button>
              <Button variant="outline" size="sm" onClick={fetchCandidates}>
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>
          }
        />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="flags" className="flex items-center gap-2"><Flag className="w-4 h-4" /> Flags</TabsTrigger>
            {isAdmin && <TabsTrigger value="config" className="flex items-center gap-2"><Settings className="w-4 h-4" /> Configuration</TabsTrigger>}
            {isAdmin && <TabsTrigger value="recipients" className="flex items-center gap-2"><Mail className="w-4 h-4" /> Email Recipients</TabsTrigger>}
          </TabsList>

          {/* ── FLAGS TAB ─────────────────────────────────────────────── */}
          <TabsContent value="flags" className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-800">Claimable Charges</h2>
              <span className="inline-flex items-center justify-center rounded-full bg-red-100 text-red-700 text-xs font-semibold h-5 min-w-[1.25rem] px-1.5">{total}</span>
            </div>
            {/* Claim summary strip */}
            {(summary.active_flags > 0 || summary.candidates > 0) && (
              <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-3">
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-red-50 border border-red-100 px-3 py-1.5 text-sm">
                  <span className="text-slate-500">Total claimable</span>
                  <span className="font-semibold text-red-700">{money(summary.total_claim_amount)}</span>
                </span>
                <button type="button" onClick={() => toggleProgress("action_required")}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors ${claimProgress === "action_required" ? "bg-teal-50 border-teal-300" : "bg-slate-50 border-slate-200 hover:border-slate-300"}`}>
                  <span className="text-slate-500">Action Required</span>
                  <span className="font-semibold text-slate-700">{summary.action_required || 0}</span>
                </button>
                <button type="button" onClick={() => toggleProgress("action_taken")}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors ${claimProgress === "action_taken" ? "bg-teal-50 border-teal-300" : "bg-slate-50 border-slate-200 hover:border-slate-300"}`}>
                  <span className="text-slate-500">Action Taken</span>
                  <span className="font-semibold text-slate-700">{summary.action_taken || 0}</span>
                </button>
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-slate-50 border border-slate-200 px-3 py-1.5 text-sm">
                  <span className="text-slate-500">Total flags</span>
                  <span className="font-semibold text-slate-700">{summary.total_flags || 0}</span>
                </span>
                <button type="button" onClick={() => toggleSource("auto")}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors ${flagSource === "auto" ? "bg-teal-50 border-teal-300" : "bg-slate-50 border-slate-200 hover:border-slate-300"}`}>
                  <span className="text-slate-500">Auto flags</span>
                  <span className="font-semibold text-slate-700">{summary.auto_flags || 0}</span>
                </button>
                <button type="button" onClick={() => toggleSource("manual")}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors ${flagSource === "manual" ? "bg-teal-50 border-teal-300" : "bg-slate-50 border-slate-200 hover:border-slate-300"}`}>
                  <span className="text-slate-500">Manual flags</span>
                  <span className="font-semibold text-slate-700">{summary.manual_flags || 0}</span>
                </button>
                {(summary.by_category || []).map((c) => {
                  const selected = flagCategory.includes(c.id);
                  return (
                    <button key={c.id} type="button" onClick={() => toggleCategory(c.id)}
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors ${selected ? "bg-teal-50 border-teal-300" : "bg-white border-slate-200 hover:border-slate-300"}`}>
                      <span className="text-slate-600">{c.name}</span>
                      <span className="font-semibold text-slate-800">{c.count}</span>
                      <span className="text-slate-400">·</span>
                      <span className="font-semibold text-red-700">{money(c.claim_amount)}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Search + collapsible filter panel */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by Order ID, PO / Reference No., or LRN..."
                  className="pl-9 h-10"
                  data-testid="commercial-flags-search"
                />
              </div>
              <Button
                variant="outline" size="icon" className="h-10 w-10 shrink-0 relative"
                onClick={() => setFiltersExpanded((v) => !v)}
                title={filtersExpanded ? "Hide filters" : "Show filters"}
                aria-pressed={filtersExpanded} data-testid="toggle-filters"
              >
                <Filter className={`w-4 h-4 ${filtersExpanded ? "text-[#00A699]" : ""}`} />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-semibold h-4 min-w-[1rem] px-1">{activeFilterCount}</span>
                )}
              </Button>
            </div>

            {filtersExpanded && (
              <div className="flex flex-wrap gap-4 rounded-lg border border-slate-200 bg-white p-4">
                <div className="w-44">
                  <Label className="text-xs">Flag State</Label>
                  <Select value={flagState} onValueChange={(v) => {
                    setFlagState(v);
                    // The Active-flags sub-filter only means anything while we are scoped
                    // to flagged orders; leaving it set would ask the backend for
                    // "not flagged AND flagged", which always returns nothing.
                    if (v !== "flagged") setClaimProgress("all");
                    resetPage();
                  }}>
                    <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="flagged">Flagged</SelectItem>
                      <SelectItem value="unflagged">Not flagged</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-44">
                  <Label className="text-xs">Flag Source</Label>
                  <Select value={flagSource} onValueChange={(v) => { setFlagSource(v); resetPage(); }}>
                    <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="auto">Auto</SelectItem>
                      <SelectItem value="manual">Manual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-60">
                  <Label className="text-xs">Flag Category</Label>
                  <div className="mt-1">
                    <SearchableMultiSelect
                      values={flagCategory}
                      onChange={(v) => { setFlagCategory(v); resetPage(); }}
                      options={flagTypes.map((t) => ({ value: t.id, label: t.name }))}
                      placeholder="All Categories"
                      emptyText="No categories"
                    />
                  </div>
                </div>
                <div className="w-60">
                  <Label className="text-xs">Seller</Label>
                  <div className="mt-1">
                    <SearchableMultiSelect values={sellerFilters} onChange={(v) => { setSellerFilters(v); resetPage(); }}
                      options={filterOptions.sellers} placeholder="All Sellers" emptyText="No sellers in this list" />
                  </div>
                </div>
                <div className="w-56">
                  <Label className="text-xs">Shipping Partner</Label>
                  <div className="mt-1">
                    <SearchableMultiSelect values={partnerFilters} onChange={(v) => { setPartnerFilters(v); resetPage(); }}
                      options={filterOptions.shipping_partners} placeholder="All Partners" emptyText="No partners in this list" />
                  </div>
                </div>
                <div className="w-52">
                  <Label className="text-xs">Status</Label>
                  <div className="mt-1">
                    <SearchableMultiSelect values={statusFilters} onChange={(v) => { setStatusFilters(v); resetPage(); }}
                      options={(filterOptions.statuses || []).map((s) => ({ value: s, label: s.replace(/_/g, " ") }))}
                      placeholder="All Statuses" emptyText="No statuses in this list" />
                  </div>
                </div>
                <div className="w-56">
                  <Label className="text-xs">Created Date</Label>
                  <div className="mt-1 grid grid-cols-2 gap-2">
                    <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); resetPage(); }} />
                    <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); resetPage(); }} />
                  </div>
                </div>
                {activeFilterCount > 0 && (
                  <div className="flex items-end">
                    <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={clearFilters}>Clear all</Button>
                  </div>
                )}
              </div>
            )}

            <DataTable
              columns={columns}
              data={rows}
              rowKey={(r) => r.order_id}
              loading={loading}
              empty={<EmptyState icon={AlertTriangle} title="No claimable charges"
                description="No LRNs have a demurrage or appointment charge on their freight bill." />}
              pagination={pages > 1 ? { page, pages, total, pageSize: ITEMS_PER_PAGE, onPageChange: setPage } : undefined}
            />
          </TabsContent>

          {/* ── CONFIGURATION TAB ─────────────────────────────────────── */}
          {isAdmin && (
            <TabsContent value="config" className="space-y-6">
              <div className="rounded-lg border border-slate-200 bg-white p-5 border-l-4 border-l-[#00A699]">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-[#00A699]" />
                  <h3 className="text-lg font-semibold text-slate-800">Check Schedule</h3>
                </div>
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <Label className="text-sm">Check Interval (minutes)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input type="number" min={1} max={1440} value={checkInterval}
                        onChange={(e) => setCheckInterval(parseInt(e.target.value) || 60)} className="h-9 w-24" />
                      <span className="text-sm text-slate-500">minutes</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Range: 1 – 1440 (24h)</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={handleUpdateSettings} disabled={savingSettings || checkInterval === settings?.check_interval_minutes} className="bg-[#00A699] hover:bg-[#008f84]">
                      {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save Interval
                    </Button>
                    <Button variant="outline" onClick={handleTriggerCheck} disabled={triggering}>
                      {triggering ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />} Run Check Now
                    </Button>
                  </div>
                </div>
                {settings && (
                  <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t text-sm text-slate-600">
                    <div><span className="text-slate-500">Current:</span> <span className="font-medium">{settings.check_interval_minutes} min</span></div>
                    {settings.last_check_at && <div><span className="text-slate-500">Last check:</span> <span className="font-medium">{formatToIST(settings.last_check_at)}</span></div>}
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-5">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Flag Types</h3>
                <div className="space-y-3">
                  {flagTypes.map((t) => (
                    <div key={t.id} className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-slate-800">{t.name}</h4>
                            <Badge className={PRIORITY_COLORS[t.priority] || "bg-slate-100 text-slate-600"}>{t.priority}</Badge>
                            <Badge variant="outline">{t.auto ? "Auto" : "Manual"}</Badge>
                            {!t.enabled && <Badge variant="outline">Disabled</Badge>}
                          </div>
                          <p className="text-sm text-slate-500">{t.description}</p>
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                          <Switch checked={t.enabled} onCheckedChange={(c) => handleToggleType(t, c)} />
                          <span className="text-sm text-slate-500">{t.enabled ? "Enabled" : "Off"}</span>
                        </div>
                      </div>
                      {t.auto && (t.claim_demurrage !== undefined || t.claim_demurrage_v2 !== undefined || t.claim_appointment !== undefined) ? (
                        <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-slate-200 pt-3">
                          <span className="text-xs font-medium text-slate-500">Auto claim:</span>
                          <label className="flex items-center gap-2 text-sm text-slate-700">
                            <Switch checked={t.claim_demurrage !== false} onCheckedChange={(c) => handleUpdateClaimComponents(t, { claim_demurrage: c })} />
                            Demurrage
                          </label>
                          <label className="flex items-center gap-2 text-sm text-slate-700">
                            <Switch checked={t.claim_demurrage_v2 !== false} onCheckedChange={(c) => handleUpdateClaimComponents(t, { claim_demurrage_v2: c })} />
                            Demurrage V2
                          </label>
                          <label className="flex items-center gap-2 text-sm text-slate-700">
                            <Switch checked={t.claim_appointment !== false} onCheckedChange={(c) => handleUpdateClaimComponents(t, { claim_appointment: c })} />
                            Appointment
                          </label>
                          <span className="text-xs text-slate-400">= {claimBasisLabel(t)}</span>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400 mt-2">Claim basis: {t.claim_basis}</p>
                      )}
                    </div>
                  ))}
                  {flagTypes.length === 0 && <p className="text-sm text-slate-500">No flag types.</p>}
                </div>
              </div>
            </TabsContent>
          )}

          {/* ── RECIPIENTS TAB ────────────────────────────────────────── */}
          {isAdmin && (
            <TabsContent value="recipients" className="space-y-6">
              <div className="rounded-lg border border-slate-200 bg-white p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-slate-800">Email Recipients</h3>
                  <Button size="sm" onClick={() => setRecipientOpen(true)}><Plus className="w-4 h-4 mr-1" /> Add Recipient</Button>
                </div>
                <p className="text-sm text-slate-500 mb-4">These addresses receive the commercial red-flag digest when new claims are raised.</p>
                {recipients.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No email recipients configured</p>
                ) : (
                  <div className="space-y-3">
                    {recipients.map((r) => (
                      <div key={r.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-800">{r.name || r.email}</p>
                          {r.name && <p className="text-sm text-slate-500">{r.email}</p>}
                          {r.created_by_name && <p className="text-xs text-slate-400 mt-1">Added by {r.created_by_name}</p>}
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            <Switch checked={r.enabled} onCheckedChange={(c) => handleToggleRecipient(r.id, c)} />
                            <span className="text-sm text-slate-500">{r.enabled ? "Active" : "Disabled"}</span>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteRecipient(r.id)} className="text-red-500 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* ── Detail / resolution-thread dialog ─────────────────────────── */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Commercial Claim</DialogTitle></DialogHeader>
          {detailFlag && (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2">
                <Badge className={PRIORITY_COLORS[detailFlag.priority] || "bg-slate-100"}>{(detailFlag.priority || "").toUpperCase()}</Badge>
                <Badge className={FLAG_STATUS_COLORS[detailFlag.status] || "bg-slate-100"}>{(detailFlag.status || "").toUpperCase()}</Badge>
                <Badge variant="outline">{detailFlag.source}</Badge>
              </div>
              <div>
                <h3 className="font-semibold text-lg">{detailFlag.title}</h3>
                <p className="text-slate-600 text-sm">{detailFlag.description}</p>
              </div>
              <div className="rounded-lg bg-red-50 border border-red-100 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Claimable amount</span>
                  <span className="text-lg font-bold text-red-700">{money(detailFlag.claim_amount)}</span>
                </div>
                {detailFlag.claim_components && ["demurrage", "demurrage_v2", "appointment", "logistics"].map((k) => {
                  const c = detailFlag.claim_components[k];
                  if (!c || !(Number(c.amount) > 0)) return null;
                  const label = { demurrage: "Demurrage", demurrage_v2: "Demurrage V2", appointment: "Appointment charge", logistics: "Full logistics (fwd + return)" }[k];
                  // 'logistics' is the entire claim — cancel the flag to drop it, not a per-component toggle.
                  const toggleable = k !== "logistics";
                  return (
                    <div key={k} className="flex items-center justify-between text-sm">
                      <span className={c.included ? "text-slate-700" : "text-slate-400 line-through"}>{label} · {money(c.amount)}</span>
                      {detailFlag.status === "active" && toggleable ? (
                        <label className="flex items-center gap-1.5 text-xs text-slate-500">
                          <Switch checked={!!c.included} onCheckedChange={(v) => adjustComponent(k, v)} />
                          {c.included ? "Claiming" : "Excluded"}
                        </label>
                      ) : (
                        <span className="text-xs text-slate-500">{c.included ? "Claimed" : "Excluded"}</span>
                      )}
                    </div>
                  );
                })}
              </div>

              {detailFlag.claim_stage === "raised_to_partner" && (
                <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 text-sm">
                  <span className="font-medium text-blue-700">Raised to Partner</span>
                  {detailFlag.claim_stage_at ? ` · ${formatToIST(detailFlag.claim_stage_at)}` : ""}
                  {detailFlag.claim_stage_by_name ? ` · by ${detailFlag.claim_stage_by_name}` : ""}
                </div>
              )}

              {detailFlag.details && (
                <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 p-4 rounded-lg">
                  {[
                    ["Order", detailFlag.entity_details?.order_number],
                    ["LRN", detailFlag.entity_details?.lrn || detailFlag.details.lrn],
                    ["Promised EDD", detailFlag.details.promised_edd],
                    ["Reached Dest.", detailFlag.details.reach_destination_at ? formatToIST(detailFlag.details.reach_destination_at) : "—"],
                    ["Delivered", detailFlag.details.delivered_at ? formatToIST(detailFlag.details.delivered_at) : "—"],
                    ["Days Late", detailFlag.details.days_late ?? "—"],
                    ["Demurrage", money(detailFlag.details.demurrage_amount)],
                    ["Appt. Charge", money(detailFlag.details.appointment_charge)],
                    ["PO / Ref", detailFlag.details.po_number || "—"],
                    ["PO Date", detailFlag.details.po_date || "—"],
                    ["PO Expiry", detailFlag.details.po_expiry_date || "—"],
                    ["Invoice", money(detailFlag.details.invoice_amount)],
                    ["Appointment", detailFlag.details.appointment_date || "—"],
                    ["All EDDs", (detailFlag.details.all_edds || []).join(", ") || "—"],
                  ].map(([k, v]) => (
                    <div key={k}><div className="text-xs text-slate-500">{k}</div><div className="font-medium">{String(v ?? "—")}</div></div>
                  ))}
                </div>
              )}

              {detailFlag.action_logs?.length > 0 && (
                <div>
                  <h4 className="font-medium text-sm mb-2">Resolution Thread</h4>
                  <div className="space-y-2">
                    {detailFlag.action_logs.map((l, i) => (
                      <div key={l.id || i} className="bg-slate-50 p-3 rounded-lg">
                        <div className="flex items-center justify-between">
                          <Badge variant="outline">{l.action_type}</Badge>
                          <span className="text-xs text-slate-500">{l.action_at ? formatToIST(l.action_at) : ""}</span>
                        </div>
                        {l.notes && <p className="text-sm text-slate-600 mt-2">{l.notes}</p>}
                        <p className="text-xs text-slate-400 mt-1">By {l.action_by_name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detailFlag.status === "active" ? (
                <div className="border-t pt-4 space-y-3">
                  <h4 className="font-medium text-sm">Take Action</h4>
                  <Select value={actionType} onValueChange={setActionType}>
                    <SelectTrigger><SelectValue placeholder="Select action" /></SelectTrigger>
                    <SelectContent>
                      {availableActions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Textarea value={actionNotes} onChange={(e) => setActionNotes(e.target.value)} placeholder="Notes (optional)" rows={2} />
                  <div className="flex justify-end gap-2">
                    <Button asChild variant="outline" size="sm">
                      <a href={orderUrl({ order_id: detailFlag.entity_id })} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-4 h-4 mr-1" /> Open order
                      </a>
                    </Button>
                    <Button onClick={handleTakeAction} disabled={submittingAction}>
                      {submittingAction ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Submit
                    </Button>
                  </div>
                </div>
              ) : detailFlag.resolved_at && (
                <div className={`p-3 rounded-lg text-sm ${detailFlag.status === "cancelled" ? "bg-rose-50" : "bg-green-50"}`}>
                  <span className={`font-medium ${detailFlag.status === "cancelled" ? "text-rose-700" : "text-green-700"}`}>{detailFlag.resolution_action}</span>{" "}
                  {formatToIST(detailFlag.resolved_at)} by {detailFlag.resolved_by_name}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Manual raise dialog ───────────────────────────────────────── */}
      <Dialog open={raiseOpen} onOpenChange={setRaiseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Raise Commercial Flag</DialogTitle>
            <DialogDescription>{raiseRow?.lrn || raiseRow?.order_number}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Category</Label>
              <Select value={raiseType} onValueChange={setRaiseType}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {raisableTypes(raiseRow).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea value={raiseNotes} onChange={(e) => setRaiseNotes(e.target.value)} placeholder="Why is this claimable?" rows={3} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRaiseOpen(false)}>Cancel</Button>
            <Button onClick={handleRaise} disabled={submittingRaise || !raiseType}>
              {submittingRaise ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Raise Flag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add recipient dialog ──────────────────────────────────────── */}
      <Dialog open={recipientOpen} onOpenChange={setRecipientOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Email Recipient</DialogTitle>
            <DialogDescription>Receives the commercial red-flag digest</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Email Address</Label><Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="email@example.com" className="mt-1" /></div>
            <div><Label>Name (optional)</Label><Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Jane Doe" className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecipientOpen(false)}>Cancel</Button>
            <Button onClick={handleAddRecipient} disabled={savingRecipient}>
              {savingRecipient ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default CommercialRedFlags;
